import type * as Contracts from './api-contracts';
import type { BehaviorAgent } from '@/app/agents/[id]/behavior/_components/behavior-form';
import { isAgentsEmbed, embedTransport } from '@/lib/agents-embed';
import {
    EntityDefinition,
    FieldDefinition,
    Record as EntityRecord,
    PaginatedResult,
    CreateEntityInput,
    UpdateEntityInput,
    CreateFieldInput,
    UpdateFieldInput
  } from '@fyso/shared';

  type AgentResponse = BehaviorAgent & Contracts.Agent & Record<string, unknown>;

  const DEFAULT_API_URL = import.meta.env.VITE_API_URL || '/api';

  /**
   * Backend origin for direct navigation (Auth0 flow).
   * Strips the /api path suffix to get the backend origin.
   * In dev: '' (relative, handled by Vite proxy)
   * In prod: 'https://api.fyso.dev'
   */
  export function getBackendOrigin(): string {
    const url = import.meta.env.VITE_API_URL;
    if (!url || url === '/api') return '';
    try {
      const parsed = new URL(url);
      return parsed.origin;
    } catch {
      return '';
    }
  }

  /** Base URL for the API (e.g. https://api.fyso.dev/api or /api in dev) */
  export function getApiUrl(): string {
    if (typeof window === 'undefined') return DEFAULT_API_URL;
    try {
      const savedTenant = localStorage.getItem('tenant');
      if (savedTenant) {
        const tenant = JSON.parse(savedTenant);
        if (tenant.deploymentType === 'dedicated' && tenant.dedicatedApiUrl) {
          return tenant.dedicatedApiUrl;
        }
      }
    } catch {
      // fall through to default
    }
    return DEFAULT_API_URL;
  }

  function getAuthHeader() {
    if (typeof window === 'undefined') return {};
    const token = localStorage.getItem('token');
    if (!token) return {};
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    // Tenant-user sessions require X-Tenant-ID to scope API requests
    if (localStorage.getItem('isTenantUser') === 'true') {
      const tenantSlug = localStorage.getItem('tenantSlug');
      if (tenantSlug) headers['X-Tenant-ID'] = tenantSlug;
    }
    return headers;
  }

  async function fetcher<T>(url: string, options?: RequestInit): Promise<T> {
    if (isAgentsEmbed()) return await embedTransport().request(url, options) as T;
    const headers = new Headers(options?.headers || {});
    headers.set('Content-Type', 'application/json');
    const auth = getAuthHeader() as Record<string, string>;
    Object.entries(auth).forEach(([key, value]) => headers.set(key, value));

    const res = await fetch(`${getApiUrl()}${url}`, {
      ...options,
      headers,
    });

    // Handle 401 Unauthorized - redirect to login
    if (res.status === 401) {
      if (globalThis.window !== undefined) {
        localStorage.clear();
        globalThis.location.href = '/login';
      }
      throw new Error('Session expired. Please login again.');
    }

    // 204 No Content — no body to parse
    if (res.status === 204) {
      return undefined as unknown as T;
    }

    const json = await res.json();

    if (!res.ok) {
      const msg = typeof json.error === 'string' ? json.error : json.error?.message || 'An error occurred';
      // status/code let callers map failures to friendly messages
      throw Object.assign(new Error(msg), {
        status: res.status,
        code: typeof json.code === 'string' ? json.code : json.error?.code,
        field: typeof json.field === 'string' ? json.field : undefined,
      });
    }

    return json.data;
  }

  // ── Agent connections (Slack / Jira / Composio) ──────────────────────
  export interface McpCatalogItem {
    id: string;
    slug: string;
    name?: string;
    description?: string;
    auth_modes?: Array<'oauth' | 'api_key'>;
    auth_note?: string;
    token_url?: string;
    documentation_url?: string;
  }
  export type AgentConnectionSlug = 'slack' | 'jira' | 'google-chat' | 'composio';

  export interface AgentConnectionInstallation {
    id: string;
    name: string | null;
    status: 'installed' | 'active' | 'inactive' | 'revoked';
    connected_agent_slug: string | null;
  }

  export interface AgentConnection {
    installation_id: string | null;
    installation_status: AgentConnectionInstallation['status'] | null;
    details: Record<string, unknown>;
    connected_at: string;
    request_url?: string;
    /** Slack Interactivity & Shortcuts Request URL (buttons, feedback, stop). */
    interactivity_url?: string;
    webhook_url?: string;
    /** Google Chat app HTTP endpoint URL. */
    endpoint_url?: string;
    jql_filter?: string;
    project_key?: string | null;
    next_steps?: string[];
  }

  export type SlackChannelMode = 'mention' | 'always' | 'proactive';

  export interface SlackChannelModeItem {
    id: string;
    name: string;
    is_private: boolean;
    /** false: saved mode for a channel the bot is no longer in */
    is_member: boolean;
    mode: SlackChannelMode;
  }

  export interface SlackChannelModes {
    agent_slug: string;
    bot_name: string | null;
    channels: SlackChannelModeItem[];
    listen: { gateModel: string | null; maxInterventionsPerHour: number };
    /** Slack error when listing channels (e.g. missing_scope) */
    list_error?: string;
  }

  export interface AgentConnections {
    agent_slug: string;
    agent_name: string;
    integrations: Array<{
      slug: AgentConnectionSlug;
      installed: boolean;
      installations: AgentConnectionInstallation[];
      connection: AgentConnection | null;
    }>;
  }

  // ── Composio connections ─────────────────────────────────────────────
  export type ComposioConnectionStatus = 'active' | 'no_active_account' | 'ambiguous_accounts';

  export interface ComposioCatalogField {
    name: string;
    display_name: string;
    type: string;
    description: string | null;
    required: boolean;
    default: string | null;
    secret: boolean;
  }

  export interface ComposioCatalogEntry {
    toolkit: string;
    name: string;
    logo: string | null;
    description: string | null;
    categories: string[];
    auth_config_id: string;
    auth_scheme: string | null;
    /** oauth → popup; fields → credentials typed in Fyso; unsupported → can't connect from Fyso */
    connect_method: 'oauth' | 'fields' | 'unsupported';
    fields: ComposioCatalogField[];
  }

  export interface ComposioConnection {
    id: string;
    installation_id: string;
    name: string;
    toolkit: string;
    auth_scheme: string | null;
    created_at: string;
    last_checked_at: string | null;
    cached_status: ComposioConnectionStatus | null;
    agent_count?: number;
  }

  export interface ComposioConnectionsState {
    installed: boolean;
    installation_id: string | null;
    installation_status: string | null;
    api_key_present: boolean;
    connections: ComposioConnection[];
  }

  export interface ComposioCheckResult {
    id: string;
    status: ComposioConnectionStatus;
    accounts: Array<{ id: string; status: string; status_reason: string | null; created_at: string | null }>;
  }

  export interface ComposioConnectResult {
    connected: boolean;
    already_connected?: boolean;
    status?: ComposioConnectionStatus;
    redirect_url?: string;
  }

  export interface ComposioToolOption {
    slug: string;
    name: string;
    description: string;
    read_only: boolean;
    recommended: boolean;
  }

  export interface ComposioBinding {
    connection_id: string;
    name: string;
    toolkit: string;
    status: ComposioConnectionStatus | null;
    last_checked_at: string | null;
    /** null = recommended tools */
    tools: string[] | null;
  }

  export interface ComposioBindings {
    agent_slug: string;
    max_tools: number;
    bindings: ComposioBinding[];
    connections: ComposioConnection[];
  }

  export interface ComposioBindingsSaved {
    bindings: ComposioBinding[];
    tool_count: number;
    tool_names: string[];
    account_problems: Array<{ connection_id: string; name: string; status: ComposioConnectionStatus }>;
    warnings: string[];
  }

  // ── Agent behavior: voice, schedules, memory ─────────────────────────
  export interface AgentVoiceStatus {
    provider_configured: boolean;
    enabled?: boolean;
    model?: string;
    voice?: string;
    voices?: string[];
    models?: string[];
    /** Voice engine: 'realtime' or 'live' (GPT-Live). */
    engine?: 'realtime' | 'live';
    live_voices?: string[];
    live_backend_models?: string[];
    /** Resolved playbooks (defaults + overrides). */
    playbooks?: Array<Record<string, unknown> & { key: string; name: string }>;
    default_playbooks?: Array<Record<string, unknown> & { key: string; name: string }>;
    /** Raw stored settings.playbooks (overrides by key). */
    playbook_overrides?: Array<Record<string, unknown> & { key: string }>;
    [key: string]: unknown;
  }

  export interface AgentSchedule {
    id: string;
    name: string;
    kind: 'heartbeat' | 'ritual' | 'followup';
    rrule: string | null;
    runAt: string | null;
    timezone: string;
    prompt: string;
    delivery: { channel: 'slack'; target: string; installationId?: string } | null;
    enabled: boolean;
    nextRunAt: string | null;
    lastRunAt: string | null;
    lastStatus: 'delivered' | 'completed' | 'silent' | 'error' | 'skipped_quiet' | 'skipped_limit' | 'skipped_disabled' | 'skipped_error' | null;
    lastError: string | null;
  }

  export interface AgentScheduleInput {
    name: string;
    kind: AgentSchedule['kind'];
    rrule: string | null;
    runAt: string | null;
    prompt: string;
    delivery: AgentSchedule['delivery'];
    enabled: boolean;
  }

  export interface AgentMemoryItem {
    id: string;
    kind: 'working' | 'self' | 'log' | 'fact' | string;
    scope: string;
    text: string;
    personId: string | null;
    createdAt: string;
    updatedAt: string;
  }

  export interface AgentPersonItem {
    id: string;
    displayName: string;
    identities: Array<{ channel: string; external_user_id: string }>;
    notes: string | null;
    lastSeenAt: string | null;
  }

  // ── Agent builder assistant ──────────────────────────────────────────
  export type AgentBuilderFocus =
    | 'all' | 'soul' | 'prompt' | 'rules' | 'tools' | 'settings' | 'voice' | 'playbooks' | 'rituals' | 'knowledge';

  export interface AgentBuilderChange {
    id: string;
    part: string;
    target: 'agent' | 'settings' | 'schedule' | 'playbook' | 'create_agent';
    operation: 'create' | 'update' | 'delete';
    schedule_id?: string;
    playbook_key?: string;
    before?: unknown;
    after: unknown;
    reason: string;
    preview?: { rrule_text?: string; before_rrule_text?: string; next_runs?: string[]; timezone?: string; voice_sample?: string };
    warnings?: string[];
    confirmation_required?: boolean;
  }

  export interface AgentBuilderProposal {
    id: string;
    agent_id: string | null;
    summary: string;
    changes: AgentBuilderChange[];
    created_at: string;
  }

  export interface AgentBuilderChatResult {
    message: string;
    proposals: AgentBuilderProposal[];
  }

  export interface AgentBuilderApplyResult {
    agent_id: string | null;
    created_agent: { id: string; slug: string; name: string } | null;
    results: Array<{ change_id: string; ok: boolean; error?: string; id?: string }>;
    applied: number;
    failed: number;
    warnings: string[];
    remaining_proposal?: AgentBuilderProposal | null;
  }

  // ── App entitlement types ─────────────────────────────────────────────
  export interface AppEntitlement {
    sourceTenantId: string;
    appKey: string;
    active: boolean;
    canCreateInstance: boolean;
    provider: string | null;
    status: 'active' | 'active_until_period_end' | 'past_due' | 'suspended' | 'refunded' | 'none' | 'free';
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    pastDueSince?: string | null;
    instancesUsed: number;
    instancesAllowed: number | null;
    displayPrice?: { amountUsd: number; billingInterval: string } | null;
  }

  export interface CreateInstanceResult {
    /** HTTP status code from the server */
    status: number;
    /** Parsed response body */
    json: {
      success: boolean;
      data?: { id: string; [key: string]: unknown };
      code?: string;
      error?: string;
    };
  }

  export interface SelectTenantResult {
    success: boolean;
    data?: {
      token: string;
      tenant: unknown;
      schemaName: string;
    };
  }

  export interface CheckoutResult {
    sourceTenantId: string;
    appKey: string;
    provider: string;
    transactionId: string;
    amountUsd: number;
    billingInterval: string;
    isReactivation: boolean;
  }

  /** Masked provider representation returned by aiProvidersService.toPublic. */
  export interface TenantAiProvider {
    id: string;
    name: string;
    provider_type: 'openai' | 'anthropic' | 'gemini';
    base_url: string;
    api_key: string;
    default_model: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    /** Optional legacy display alias; current backend uses provider_type. */
    type?: string;
  }

  /** Serialized tenant_registry channel returned by GET /channels/mine. */
  export interface ApiChannel {
    id: string;
    tenant_id: string;
    name: string;
    slug: string;
    description: string;
    tags: string[];
    metadata?: Record<string, unknown>;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  }

  /** Channel fields consumed by legacy agent-channel views. */
  export interface AgentChannelView {
    id: string;
    channelId?: string;
    name?: string;
    type?: string;
    channel?: { name?: string; type?: string; isActive?: boolean };
  }

  export interface AgentChannelsResponse {
    agentId: string;
    agentSlug: string;
    channels: Array<{
      id: string;
      channelType: string;
      isActive: boolean;
      config: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
    }>;
  }

  /** Serialized _fyso_agent_versions row. */
  export interface AgentVersionResponse {
    id: string;
    agentId: string;
    versionNumber: number;
    systemPrompt: string;
    rules: unknown[];
    changedAt: string;
    changedBy: string | null;
    /** Legacy presentation fields are not supplied by the current endpoint. */
    version?: number;
    createdAt?: string;
    isCurrent?: boolean;
    promptPreview?: string;
  }

  /** Serialized entity_definitions row, without the metadata route's enrichment. */
  export interface EntityDefinitionRow {
    id: string;
    name: string;
    tableName: string;
    displayName: string | null;
    description: string | null;
    icon: string | null;
    titleField: string | null;
    isSystem: boolean | null;
    realtimeEnabled: boolean;
    metadata: unknown;
    version: number;
    status: string;
    publishedVersion: number | null;
    publishedAt: string | null;
    publishedBy: string | null;
    createdAt: string | null;
    updatedAt: string | null;
  }

  export interface EntityVersionSnapshot {
    version: number;
    publishedAt?: string;
    publishedBy?: string;
    entity: {
      name: string;
      tableName: string;
      displayName?: string;
      description?: string;
      icon?: string;
      titleField?: string;
      metadata?: Record<string, unknown>;
    };
    fields: Array<{
      name: string;
      fieldKey: string;
      fieldType: string;
      description?: string;
      config?: Record<string, unknown>;
      isRequired?: boolean;
      isUnique?: boolean;
      isSystem?: boolean;
      displayOrder?: number;
    }>;
  }

  export interface EntityVersionDiff {
    fromVersion: number;
    toVersion: number;
    entity: { changed: string[]; added: string[]; removed: string[] };
    fields: {
      added: string[];
      removed: string[];
      modified: Record<string, Record<string, { from: unknown; to: unknown }>>;
    };
  }

  export interface EntityVersionSummary {
    version: number;
    publishedAt: string;
    publishedBy: string | null;
    notes: string | null;
    hasChanges: boolean;
  }

  export interface EntityVersionDetails {
    id: string;
    entityId: string;
    version: number;
    snapshot: EntityVersionSnapshot;
    changes: EntityVersionDiff | null;
    notes: string | null;
    publishedAt: string;
    publishedBy: string | null;
    createdAt: string | null;
  }

  export interface FlagStatusPayload {
    key: string; label: string; description: string;
    /** What applies to this user right now. */
    value: boolean;
    /** What applies with no user override. */
    deployValue: boolean;
    overridden: boolean;
  }
  export interface FlagsPayload {
    flags: Record<string, boolean>;
    items: FlagStatusPayload[];
    /** false for credentials without a user: the deploy values, read-only. */
    editable: boolean;
  }

  export const api = {
    flags: {
      get: () => fetcher<FlagsPayload>('/auth/flags'),
      set: (key: string, value: boolean) =>
        fetcher<FlagsPayload>('/auth/flags', { method: 'PATCH', body: JSON.stringify({ key, value }) }),
    },
    agentWorkspace: {
      external: () => fetcher<Array<{
        agentId: string; name: string; registeredAt: string | null;
        lastSeenAt: string | null; lastHeartbeatAt: string | null;
        heartbeatTtl: number | null; heartbeatStatus: string | null; isStale: boolean | null;
      }>>('/agent-workspace/external'),
    },
    metadata: {
      getEntities: (options?: { includeDrafts?: boolean }) => {
        const query = options?.includeDrafts ? '?include=drafts' : '';
        return fetcher<EntityDefinition[]>(`/metadata/entities${query}`);
      },
      getEntity: (name: string) => fetcher<EntityDefinition>(`/metadata/entities/${name}`),
      getDraft: (name: string) => fetcher<EntityDefinition>(`/metadata/entities/${name}/draft`),
      createEntity: (data: CreateEntityInput) => fetcher<EntityDefinition>('/metadata/entities', { method: 'POST', body: JSON.stringify(data) }),
      updateEntity: (id: string, data: UpdateEntityInput) => fetcher<EntityDefinition>(`/metadata/entities/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      deleteEntity: (id: string) => fetcher<void>(`/metadata/entities/${id}`, { method: 'DELETE' }),
      publishEntity: (name: string, notes?: string) => fetcher<{ entity: EntityDefinitionRow; snapshot: EntityVersionSnapshot; changes: EntityVersionDiff | null }>(`/metadata/entities/${name}/publish`, { method: 'POST', body: JSON.stringify({ notes }) }),
      discardDraft: (name: string) => fetcher<EntityDefinitionRow | null>(`/metadata/entities/${name}/draft`, { method: 'DELETE' }),
      listVersions: (name: string) => fetcher<EntityVersionSummary[]>(`/metadata/entities/${name}/versions`),
      getVersion: (name: string, version: number) => fetcher<EntityVersionDetails>(`/metadata/entities/${name}/versions/${version}`),
      getVersionDiff: (name: string, version: number) => fetcher<EntityVersionDiff>(`/metadata/entities/${name}/versions/${version}/diff`),

      addField: (entityId: string, data: CreateFieldInput) => fetcher<FieldDefinition>(`/metadata/entities/${entityId}/fields`, { method: 'POST', body: JSON.stringify(data) }),
      updateField: (fieldId: string, data: UpdateFieldInput) => fetcher<FieldDefinition>(`/metadata/fields/${fieldId}`, { method: 'PUT', body: JSON.stringify(data) }),
      deleteField: (fieldId: string) => fetcher<void>(`/metadata/fields/${fieldId}`, { method: 'DELETE' }),
      embeddingsStats: () => fetcher<Contracts.EmbeddingStats>('/metadata/embeddings/stats'),

      exportMetadata: async () => {
        const headers = getAuthHeader() as Record<string, string>;
        const res = await fetch(`${getApiUrl()}/metadata/export`, { headers });

        if (res.status === 401) {
          if (globalThis.window !== undefined) {
            localStorage.clear();
            globalThis.location.href = '/login';
          }
          throw new Error('Session expired. Please login again.');
        }

        if (!res.ok) {
          const json = await res.json();
          throw new Error(json.error?.message || 'Export failed');
        }

        const contentType = res.headers.get('Content-Type');
        const isGzip = contentType === 'application/gzip';
        const blob = await res.blob();

        return { blob, isGzip };
      },

      importMetadata: async (file: File) => {
        const arrayBuffer = await file.arrayBuffer();
        const headers = getAuthHeader() as Record<string, string>;

        // Detect if file is gzipped
        if (file.name.endsWith('.gz')) {
          headers['Content-Type'] = 'application/gzip';
          headers['Content-Encoding'] = 'gzip';
        } else {
          headers['Content-Type'] = 'application/json';
        }

        const res = await fetch(`${getApiUrl()}/metadata/import`, {
          method: 'POST',
          headers,
          body: arrayBuffer
        });

        if (res.status === 401) {
          if (globalThis.window !== undefined) {
            localStorage.clear();
            globalThis.location.href = '/login';
          }
          throw new Error('Session expired. Please login again.');
        }

        const json = await res.json();

        if (!res.ok) {
          throw new Error(json.error?.message || 'Import failed');
        }

        return json.data;
      },
    },
    rules: {
      list: (entityName: string, options?: { includeDrafts?: boolean }) => {
        const query = options?.includeDrafts ? '?include=drafts' : '';
        return fetcher<Contracts.RuleDetail[]>(`/rules/${entityName}${query}`);
      },
      get: (entityName: string, ruleId: string) => fetcher<Contracts.RuleDetail>(`/rules/${entityName}/${ruleId}`),
      create: (entityName: string, data: unknown) => fetcher<Contracts.RuleDetail>(`/rules/${entityName}`, { method: 'POST', body: JSON.stringify(data) }),
      update: (entityName: string, ruleId: string, data: unknown) => fetcher<Contracts.RuleDetail>(`/rules/${entityName}/${ruleId}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (entityName: string, ruleId: string) => fetcher<void>(`/rules/${entityName}/${ruleId}`, { method: 'DELETE' }),
      publish: (entityName: string, ruleId: string, notes?: string) => fetcher<Contracts.RuleDetail>(`/rules/${entityName}/${ruleId}/publish`, { method: 'POST', body: JSON.stringify({ notes }) }),
      discardDraft: (entityName: string, ruleId: string) => fetcher<Contracts.RuleDetail>(`/rules/${entityName}/${ruleId}/draft`, { method: 'DELETE' }),
      test: (entityName: string, ruleId: string, testContext: unknown) => fetcher<Contracts.RuleTestResult>(`/rules/${entityName}/${ruleId}/test`, { method: 'POST', body: JSON.stringify({ testContext }) }),
      logs: (entityName: string, query?: { record_id?: string; rule_id?: string; status?: string; since?: string; limit?: number; offset?: number }) => {
        const params = new URLSearchParams();
        if (query?.record_id) params.set('record_id', query.record_id);
        if (query?.rule_id) params.set('rule_id', query.rule_id);
        if (query?.status) params.set('status', query.status);
        if (query?.since) params.set('since', query.since);
        if (query?.limit) params.set('limit', String(query.limit));
        if (query?.offset) params.set('offset', String(query.offset));
        return fetcher<{ data: Contracts.RuleLog[]; total: number }>(`/rules/${entityName}/logs?${params.toString()}`);
      },
      logDetail: (entityName: string, logId: string) => fetcher<Contracts.RuleLog>(`/rules/${entityName}/logs/${logId}`),
      purgeLogs: (entityName: string, before: string) => fetcher<{ deleted: number }>(`/rules/${entityName}/logs?before=${before}`, { method: 'DELETE' }),
    },
    records: {
      list: (entityName: string, query?: Record<string, string>) => {
        const params = new URLSearchParams(query);
        return fetcher<PaginatedResult<EntityRecord>>(`/entities/${entityName}/records?${params.toString()}`);
      },
      get: (entityName: string, id: string) => fetcher<EntityRecord>(`/entities/${entityName}/records/${id}`),
      create: (entityName: string, data: Record<string, unknown>) => fetcher<EntityRecord>(`/entities/${entityName}/records`, { method: 'POST', body: JSON.stringify(data) }),
      update: (entityName: string, id: string, data: Record<string, unknown>) => fetcher<EntityRecord>(`/entities/${entityName}/records/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (entityName: string, id: string) => fetcher<void>(`/entities/${entityName}/records/${id}`, { method: 'DELETE' }),
      importCsvPreview: async (entityName: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        const headers = getAuthHeader() as Record<string, string>;
        const res = await fetch(`${getApiUrl()}/entities/${entityName}/import/preview`, {
          method: 'POST',
          headers,
          body: formData,
        });
        if (res.status === 401) {
          if (globalThis.window !== undefined) { localStorage.clear(); globalThis.location.href = '/login'; }
          throw new Error('Session expired. Please login again.');
        }
        const json = await res.json();
        if (!res.ok) throw new Error(json.error?.message || 'Preview failed');
        return json.data;
      },
      importCsv: async (entityName: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        const headers = getAuthHeader() as Record<string, string>;
        const res = await fetch(`${getApiUrl()}/entities/${entityName}/import`, {
          method: 'POST',
          headers,
          body: formData,
        });
        if (res.status === 401) {
          if (globalThis.window !== undefined) { localStorage.clear(); globalThis.location.href = '/login'; }
          throw new Error('Session expired. Please login again.');
        }
        const json = await res.json();
        if (!res.ok) throw new Error(json.error?.message || 'Import failed');
        return json.data;
      },
      exportCsv: async (entityName: string, query?: Record<string, string>) => {
        const params = query ? new URLSearchParams(query) : new URLSearchParams();
        const headers = getAuthHeader() as Record<string, string>;
        const res = await fetch(`${getApiUrl()}/entities/${entityName}/export/csv?${params.toString()}`, { headers });

        if (res.status === 401) {
          if (globalThis.window !== undefined) {
            localStorage.clear();
            globalThis.location.href = '/login';
          }
          throw new Error('Session expired. Please login again.');
        }

        if (!res.ok) {
          throw new Error('Export failed');
        }

        return res.blob();
      },
    },
    feedback: {
      submit: (data: { type: string; message: string; email?: string; pageUrl?: string }) =>
        fetcher<{ id: string }>('/feedback', { method: 'POST', body: JSON.stringify(data) }),
    },
    orgs: {
      list: () => fetcher<Contracts.Org[]>('/orgs'),
      fysoTeamsWorkspaces: () => fetcher<{
        organizations: Array<{
          id: string | null;
          name: string;
          slug: string | null;
          isPersonal: boolean;
          plan: string | null;
          orgRole: string | null;
          workspaces: Contracts.FysoTeamsWorkspace[];
        }>;
        workspaces: Array<{
          tenantId: string;
          tenantName: string;
          tenantSlug: string;
          orgId: string | null;
          orgName: string | null;
          orgSlug: string | null;
          orgIsPersonal: boolean;
          orgPlan: string | null;
          orgRole: string | null;
          tenantUserRole: string | null;
          tenantUserIsOwner: boolean;
          accessMode: 'admin' | 'tenant_user' | 'org_member';
          canEnter: boolean;
          entitlementStatus: string;
        }>;
      }>('/orgs/fyso-teams/workspaces'),
      selectFysoTeamsWorkspace: (tenantId: string) => fetcher<{
        accessMode: 'admin' | 'tenant_user';
        token: string;
        tenant: Contracts.Tenant;
        schemaName: string;
        user?: {
          id: string;
          email: string;
          name: string;
          isOwner: boolean;
        };
      }>(`/orgs/fyso-teams/workspaces/${tenantId}/select`, { method: 'POST' }),
      get: (orgId: string) => fetcher<Contracts.Org>(`/orgs/${orgId}`),
      create: (name: string) => fetcher<Contracts.Org[]>('/orgs', {
        method: 'POST',
        body: JSON.stringify({ name }),
      }),
      update: (orgId: string, data: { name?: string; slug?: string }) => fetcher<Contracts.Org>(`/orgs/${orgId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
      delete: (orgId: string) => fetcher<Contracts.Org>(`/orgs/${orgId}`, { method: 'DELETE' }),
      select: (orgId: string) => fetcher<unknown>(`/orgs/${orgId}/select`, { method: 'POST' }),
      members: {
        list: (orgId: string) => fetcher<unknown[]>(`/orgs/${orgId}/members`),
        invite: (orgId: string, email: string, role: string = 'member') => fetcher<unknown>(`/orgs/${orgId}/members/invite`, {
          method: 'POST',
          body: JSON.stringify({ email, role }),
        }),
        remove: (orgId: string, userId: string) => fetcher<unknown>(`/orgs/${orgId}/members/${userId}`, { method: 'DELETE' }),
        updateRole: (orgId: string, userId: string, role: string) => fetcher<unknown>(`/orgs/${orgId}/members/${userId}`, {
          method: 'PATCH',
          body: JSON.stringify({ role }),
        }),
        leave: (orgId: string) => fetcher<unknown>(`/orgs/${orgId}/members/me`, { method: 'DELETE' }),
      },
      invitations: {
        list: (orgId: string) => fetcher<unknown[]>(`/orgs/${orgId}/invitations`),
        revoke: (orgId: string, invitationId: string) => fetcher<unknown>(`/orgs/${orgId}/invitations/${invitationId}`, { method: 'DELETE' }),
      },
      tenants: {
        list: (orgId: string) => fetcher<unknown[]>(`/orgs/${orgId}/tenants`),
      },
    },
    tenants: {
      list: (params?: { org_id?: string }) => {
        const query = params?.org_id ? `?org_id=${params.org_id}` : '';
        return fetcher<Array<Contracts.Tenant & Contracts.TenantSettings>>(`/auth/tenants${query}`);
      },
      myTenants: () => fetcher<Array<{ slug: string; name: string; roleName: string; isOwner: boolean; id?: string; schemaCreated?: boolean }>>('/auth/tenant/my-tenants'),
      updateSettings: (id: string, data: {
        name?: string;
        primaryColor?: string | null;
        appName?: string | null;
        logoUrl?: string | null;
        selfRegistrationEnabled?: boolean;
        passwordResetEnabled?: boolean;
      }) => fetcher<Contracts.TenantSettings>(`/auth/tenants/${id}/settings`, { method: 'PUT', body: JSON.stringify(data) }),
      getAiSettings: (id: string) => fetcher<Contracts.AiSettings>(`/auth/tenants/${id}/ai-settings`),
      saveAiSettings: (id: string, data: {
        providers?: {
          default?: {
            type?: string;
            base_url?: string;
            api_key?: string;
            default_model?: string;
          };
        };
        limits?: {
          max_calls_per_hour?: number;
          max_tokens_per_call?: number;
          monthly_budget_usd?: number;
        };
        debug?: boolean;
      }) => fetcher<Contracts.AiSettings>(`/auth/tenants/${id}/ai-settings`, { method: 'PUT', body: JSON.stringify(data) }),
      testAiConnection: (id: string) => fetcher<{
        status: 'ok';
        test_latency_ms: number;
        models_available: string[];
      }>(`/auth/tenants/${id}/ai-settings/test`, { method: 'POST' }),
      /**
       * POST /api/auth/tenants — create an app instance tenant.
       * Returns raw status + body so callers can handle 402 / 409 edge cases.
       */
      createInstance: async (data: {
        name: string;
        sourceTenantId: string;
        orgId?: string;
      }): Promise<CreateInstanceResult> => {
        const auth = getAuthHeader() as Record<string, string>;
        const res = await fetch(`${getApiUrl()}/auth/tenants`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...auth },
          body: JSON.stringify({
            mode: 'instance',
            source_tenant_id: data.sourceTenantId,
            name: data.name,
            org_id: data.orgId,
          }),
        });
        if (res.status === 401) {
          if (globalThis.window !== undefined) {
            localStorage.clear();
            globalThis.location.href = '/login';
          }
          throw new Error('Session expired. Please login again.');
        }
        const json = await res.json();
        return { status: res.status, json };
      },
      /**
       * POST /api/auth/tenants/:id/select — switch active tenant session.
       * Returns the full body (token, tenant, schemaName).
       */
      selectTenant: async (tenantId: string): Promise<SelectTenantResult> => {
        const auth = getAuthHeader() as Record<string, string>;
        const res = await fetch(`${getApiUrl()}/auth/tenants/${tenantId}/select`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...auth },
        });
        if (res.status === 401) {
          if (globalThis.window !== undefined) {
            localStorage.clear();
            globalThis.location.href = '/login';
          }
          throw new Error('Session expired. Please login again.');
        }
        const json = await res.json();
        return json as SelectTenantResult;
      },
    },
    sites: {
      list: () => fetcher<Contracts.Site[]>('/sites'),
      delete: (subdomain: string) => fetcher<unknown>(`/sites/${subdomain}`, { method: 'DELETE' }),
      getCustomDomain: (subdomain: string) => fetcher<Contracts.CustomDomain>(`/sites/${subdomain}/domains`),
      setCustomDomain: (subdomain: string, domain: string) =>
        fetcher<Contracts.CustomDomain>(`/sites/${subdomain}/domains`, { method: 'POST', body: JSON.stringify({ domain }) }),
      removeCustomDomain: (subdomain: string) =>
        fetcher<void>(`/sites/${subdomain}/domains`, { method: 'DELETE' }),
      verifyCustomDomain: (subdomain: string) =>
        fetcher<Contracts.CustomDomain>(`/sites/${subdomain}/domains/verify`, { method: 'POST' }),
      getAnalytics: (subdomain: string, period: '7d' | '30d' = '7d') =>
        fetcher<unknown>(`/sites/${subdomain}/analytics?period=${period}`),
    },
    users: {
      list: () => fetcher<Contracts.TenantUser[]>('/auth/tenant/users'),
      create: (data: { email: string; password: string; name: string; role?: string; permissions?: unknown }) =>
        fetcher<Contracts.TenantUser>('/auth/tenant/users', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: unknown) => fetcher<unknown>(`/auth/tenant/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: string) => fetcher<void>(`/auth/tenant/users/${id}`, { method: 'DELETE' }),
      resendVerification: (id: string) => fetcher<void>(`/auth/tenant/users/${id}/resend-verification`, { method: 'POST' }),
    },
    logs: {
      list: (query?: { method?: string; status?: string; since?: string; entity?: string; limit?: number; offset?: number }) => {
        const params = new URLSearchParams();
        if (query?.method) params.set('method', query.method);
        if (query?.status) params.set('status', query.status);
        if (query?.since) params.set('since', query.since);
        if (query?.entity) params.set('entity', query.entity);
        if (query?.limit) params.set('limit', String(query.limit));
        if (query?.offset) params.set('offset', String(query.offset));
        return fetcher<Contracts.RequestLog[]>(`/logs?${params.toString()}`);
      },
      stats: () => fetcher<unknown>('/logs/stats'),
    },
    channels: {
      mine: () => fetcher<ApiChannel>('/channels/mine'),
      getTools: (slug: string) => fetcher<Contracts.ChannelTool[]>(`/channels/${slug}/tools`),
    },
    tools: {
      mine: () => fetcher<Contracts.McpTool[]>('/tools/mine'),
    },
    apps: {
      mine: () => fetcher<Contracts.PublishedApp[]>('/apps/mine'),
      publish: (data: { name: string; slug: string; description?: string; icon?: string }) =>
        fetcher<unknown>('/apps/publish', { method: 'POST', body: JSON.stringify(data) }),
      update: (slug: string, data: { name?: string; description?: string; icon?: string; refreshMetadata?: boolean }) =>
        fetcher<unknown>(`/apps/manage/${slug}`, { method: 'PUT', body: JSON.stringify(data) }),
      unpublish: (slug: string) => fetcher<unknown>(`/apps/manage/${slug}`, { method: 'DELETE' }),
    },
    pdf: {
      listTemplates: () => fetcher<Contracts.PdfTemplate[]>('/pdf/templates'),
      generate: async (data: { templateId: string; entityName?: string; recordId?: string; data?: unknown; store?: boolean }) => {
        const headers = new Headers();
        headers.set('Content-Type', 'application/json');
        const auth = getAuthHeader() as Record<string, string>;
        Object.entries(auth).forEach(([key, value]) => headers.set(key, value));

        const res = await fetch(`${getApiUrl()}/pdf/generate`, {
          method: 'POST',
          headers,
          body: JSON.stringify(data),
        });

        if (data.store) {
          const json = await res.json();
          if (!res.ok) throw new Error(json.error?.message || 'PDF generation failed');
          return json.data;
        }

        const json = await res.json();
        if (!res.ok) throw new Error(json.error?.message || 'PDF generation failed');

        // Backend returns base64-encoded PDF — decode to Blob
        const byteChars = atob(json.data.base64);
        const byteArray = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
        return new Blob([byteArray], { type: 'application/pdf' });
      },
    },
    dedicated: {
      health: async (dedicatedApiUrl: string) => {
        const headers = getAuthHeader() as Record<string, string>;
        const res = await fetch(`${dedicatedApiUrl}/api/health/detailed`, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        return json.data || json;
      },
    },
    knowledge: {
      stats: () => fetcher<Contracts.KnowledgeStats>('/knowledge/stats'),
      list: () => fetcher<Contracts.KnowledgeDocument[]>('/knowledge/documents'),
      create: (data: { title: string; content: string; source_type: string; mime_type?: string; metadata?: unknown }) =>
        fetcher<Contracts.KnowledgeDocument>('/knowledge/documents', { method: 'POST', body: JSON.stringify(data) }),
      get: (id: string) => fetcher<Contracts.KnowledgeContent>(`/knowledge/documents/${id}`),
      delete: (id: string) => fetcher<void>(`/knowledge/documents/${id}`, { method: 'DELETE' }),
      search: (data: { query: string; limit?: number; threshold?: number; include_records?: boolean; one_per_document?: boolean }) =>
        fetcher<Contracts.SearchResult[] & { results?: Contracts.SearchResult[] }>('/knowledge/search', { method: 'POST', body: JSON.stringify(data) })
          .then((res) => (res?.results ?? res)),
      reindex: () => fetcher<unknown>('/knowledge/reindex', { method: 'POST' }),
      reindexRecords: () => fetcher<unknown>('/knowledge/reindex-records', { method: 'POST' }),
      upload: async (file: File, title?: string) => {
        const formData = new FormData();
        formData.append('file', file);
        if (title) formData.append('title', title);
        const headers = getAuthHeader() as Record<string, string>;
        const res = await fetch(`${getApiUrl()}/knowledge/documents/upload`, {
          method: 'POST',
          headers,
          body: formData,
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'Upload failed');
        return json.data;
      },
    },
    webhooks: {
      list: (entity?: string) => {
        const query = entity ? `?entity=${entity}` : '';
        return fetcher<Contracts.WebhookSubscription[]>(`/webhooks/subscriptions${query}`);
      },
      get: (id: string) => fetcher<unknown>(`/webhooks/subscriptions/${id}`),
      create: (data: { url: string; entity_name: string; event_types: string[]; secret?: string; description?: string }) =>
        fetcher<unknown>('/webhooks/subscriptions', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: { is_active?: boolean; url?: string; event_types?: string[]; secret?: string; description?: string }) =>
        fetcher<unknown>(`/webhooks/subscriptions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: string) => fetcher<unknown>(`/webhooks/subscriptions/${id}`, { method: 'DELETE' }),
      deliveries: (id: string, limit?: number) => {
        const query = limit ? `?limit=${limit}` : '';
        return fetcher<Contracts.Delivery[]>(`/webhooks/subscriptions/${id}/deliveries${query}`);
      },
    },
    billing: {
      getUsage: () => fetcher<Contracts.UsageData>('/billing/usage'),
      // getPlan removed — use api.orgs.get(orgId) instead
      createCheckout: (orgId: string) => fetcher<{ url?: string; transactionId?: string }>('/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({ org_id: orgId }),
      }),
      createPortal: (orgId: string) => fetcher<{ url: string }>('/billing/portal', {
        method: 'POST',
        body: JSON.stringify({ org_id: orgId }),
      }),
    },
    appEntitlements: {
      /**
       * GET /api/auth/app-entitlements/:sourceTenantId
       * Returns entitlement state for the admin's org.
       */
      get: (sourceTenantId: string, orgId?: string): Promise<AppEntitlement> => {
        const params = orgId ? `?orgId=${orgId}` : '';
        return fetcher<AppEntitlement>(`/auth/app-entitlements/${sourceTenantId}${params}`);
      },
      /**
       * POST /api/auth/app-entitlements/:sourceTenantId/checkout
       * Creates a Paddle checkout transaction.
       */
      checkout: (sourceTenantId: string, orgId?: string): Promise<CheckoutResult> =>
        fetcher<CheckoutResult>(`/auth/app-entitlements/${sourceTenantId}/checkout`, {
          method: 'POST',
          body: JSON.stringify({ orgId }),
        }),
    },
    apiKeys: {
      list: () => fetcher<Contracts.ApiKeyRecord[]>('/auth/api-keys'),
      create: (data: { roleId: string; label?: string; ttlDays?: number }) =>
        fetcher<Contracts.ApiKeyRecord & { key: string }>('/auth/api-keys', { method: 'POST', body: JSON.stringify(data) }),
      revoke: (id: string) => fetcher<unknown>(`/auth/api-keys/${id}`, { method: 'DELETE' }),
    },
    events: {
      list: (query?: { type?: string; since?: string; limit?: number; offset?: number }) => {
        const params = new URLSearchParams();
        if (query?.type) params.set('type', query.type);
        if (query?.since) params.set('since', query.since);
        if (query?.limit) params.set('limit', String(query.limit));
        if (query?.offset) params.set('offset', String(query.offset));
        return fetcher<Contracts.ActivityEvent[]>(`/events?${params.toString()}`);
      },
      types: () => fetcher<string[]>('/events/types'),
    },
    emailLogs: {
      list: (query?: { type?: string; status?: string; limit?: number; offset?: number }) => {
        const params = new URLSearchParams();
        if (query?.type) params.set('type', query.type);
        if (query?.status) params.set('status', query.status);
        if (query?.limit) params.set('limit', String(query.limit));
        if (query?.offset) params.set('offset', String(query.offset));
        return fetcher<Contracts.EmailLog[]>(`/email-logs?${params.toString()}`);
      },
    },
    mcpProfiles: {
      list: () => fetcher<Contracts.ToolProfile[]>('/auth/tool-profiles'),
      create: (data: { name: string; description?: string; tools: string[] }) =>
        fetcher<Contracts.ToolProfile>('/auth/tool-profiles', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: { name?: string; description?: string; tools?: string[] }) =>
        fetcher<unknown>(`/auth/tool-profiles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: string) => fetcher<void>(`/auth/tool-profiles/${id}`, { method: 'DELETE' }),
      assignToUser: (userId: string, profileId: string | null) =>
        fetcher<unknown>(`/auth/tenant/users/${userId}/tool-profile`, {
          method: 'PUT',
          body: JSON.stringify({ toolProfileId: profileId }),
      }),
    },
    mcpDirect: {
      assignConnection: (agentId: string, connectionId: string) => fetcher<{ assigned: boolean }>(`/mcp/agents/${agentId}/connections/${connectionId}`, { method: 'POST' }),
      unassignConnection: (agentId: string, connectionId: string) => fetcher<{ assigned: boolean }>(`/mcp/agents/${agentId}/connections/${connectionId}`, { method: 'DELETE' }),
      catalog: (scope?: 'templates') => fetcher<{ items: McpCatalogItem[] }>(`/mcp/catalog${scope ? `?scope=${scope}` : ''}`),
      createCustom: (body: { name: string; slug: string; url: string }) => fetcher<{ id: string }>('/mcp/connectors/custom', { method: 'POST', body: JSON.stringify(body) }),
      publishConnector: (id: string) => fetcher<unknown>(`/mcp/connectors/${id}/publish`, { method: 'POST' }),
      createConnection: (body: { connector_id: string; name: string; auth_mode: 'oauth' | 'api_key'; secret: Record<string, unknown> }) => fetcher<{ id: string }>('/mcp/connections', { method: 'POST', body: JSON.stringify(body) }),
      startOAuth: (id: string) => fetcher<{ authorizationUrl: string } | { authorized: true; status: 'connected' }>(`/mcp/connections/${id}/oauth/start`, { method: 'POST' }),
      reauthorize: (id: string) => fetcher<{ authorizationUrl: string } | { authorized: true; status: 'connected' }>(`/mcp/connections/${id}/reauthorize`, { method: 'POST' }),
      disconnect: (id: string) => fetcher<{ id: string }>(`/mcp/connections/${id}/revoke`, { method: 'POST' }),
      testConnection: (id: string) => fetcher<unknown>(`/mcp/connections/${id}/test`, { method: 'POST' }),
      renameConnection: (id: string, name: string) => fetcher<{ id: string; name: string }>(`/mcp/connections/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
      deleteConnection: (id: string) => fetcher<{ id: string }>(`/mcp/connections/${id}`, { method: 'DELETE' }),
      listBindings: (agentId: string) => fetcher<{ items: Array<{ connection_id: string; enabled: boolean; tools: Array<{ name: string; executionPolicy: 'read' | 'action' }> }> }>(`/mcp/agents/${agentId}/bindings`),
      capability: () => fetcher<{ enabled: boolean; ready?: boolean; reason?: string; message?: string }>('/mcp/capability'),
      listConnections: () => fetcher<{ items: Array<{ id: string; name: string; status: string; auth_mode: 'oauth' | 'api_key'; connector_name?: string; connector_slug?: string; connector_custom?: boolean }> }>('/mcp/connections'),
      listTools: (connectionId: string) => fetcher<{ items: Array<{ name: string; schemaHash: string; executionPolicy: 'read' | 'action' }> }>(`/mcp/connections/${connectionId}/tools`),
      bindAgent: (agentId: string, connectionId: string, tools: Array<{ name: string; schema_hash: string; execution_policy: 'read' | 'action' }>) => fetcher<unknown>(`/mcp/agents/${agentId}/bindings/${connectionId}`, {
        method: 'PUT',
        body: JSON.stringify({ enabled: true, tools }),
      }),
    },
    roles: {
      list: () => fetcher<Contracts.Role[]>('/roles'),
      get: (id: string) => fetcher<Contracts.Role>(`/roles/${id}`),
      create: (data: { name: string; description?: string; permissions: unknown }) =>
        fetcher<Contracts.Role>('/roles', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: { name?: string; description?: string; permissions?: unknown }) =>
        fetcher<Contracts.Role>(`/roles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: string) => fetcher<void>(`/roles/${id}`, { method: 'DELETE' }),
      getUserRoles: (userId: string) => fetcher<Contracts.UserRole[]>(`/roles/users/${userId}/roles`),
      batchGetUserRoles: (userIds: string[]) =>
        fetcher<Record<string, Contracts.UserRole[]>>(`/roles/users/batch?ids=${userIds.join(',')}`),
      assignRole: (userId: string, roleId: string) =>
        fetcher<Contracts.UserRole[]>(`/roles/users/${userId}/roles`, { method: 'POST', body: JSON.stringify({ roleId }) }),
      revokeRole: (userId: string, roleId: string) =>
        fetcher<void>(`/roles/users/${userId}/roles/${roleId}`, { method: 'DELETE' }),
      auditLog: (roleId: string, limit = 50) =>
        fetcher<unknown[]>(`/roles/${roleId}/audit?limit=${limit}`),
    },
    usageDashboard: {
      quota: () => fetcher<{ storageBytes?: Contracts.UsageItem }>('/usage'),
      metrics: () => fetcher<{ plan?: { usage?: Contracts.UsageData } }>('/usage/dashboard'),
    },
    invitations: {
      list: () => fetcher<Contracts.Invitation[]>('/invitations'),
      create: (data: { role?: string; expiresInDays?: number; email?: string }) =>
        fetcher<Contracts.Invitation>('/invitations', { method: 'POST', body: JSON.stringify(data) }),
      revoke: (id: string) => fetcher<void>(`/invitations/${id}`, { method: 'DELETE' }),
      sendEmail: (id: string, email: string) =>
        fetcher<unknown>(`/invitations/${id}/send`, { method: 'POST', body: JSON.stringify({ email }) }),
    },
    platformInvitations: {
      list: () => fetcher<{ invitations: Contracts.PlatformInvitation[]; quota: Contracts.QuotaInfo }>('/platform/invitations'),
      create: (data: { email: string }) =>
        fetcher<{ invitations: Contracts.PlatformInvitation[]; quota: Contracts.QuotaInfo }>('/platform/invitations', { method: 'POST', body: JSON.stringify(data) }),
      revoke: (id: string) => fetcher<void>(`/platform/invitations/${id}`, { method: 'DELETE' }),
    },
    views: {
      list: () => fetcher<Contracts.View[]>('/views'),
      get: (slug: string) => fetcher<Contracts.View & { entityName?: string }>(`/views/${slug}`),
      create: (data: { slug: string; entitySlug: string; name: string; description?: string; filterDsl?: unknown; isActive?: boolean }) =>
        fetcher<Contracts.View[]>('/views', { method: 'POST', body: JSON.stringify(data) }),
      update: (slug: string, data: { name?: string; description?: string; filterDsl?: unknown; isActive?: boolean }) =>
        fetcher<Contracts.View & { entityName?: string }>(`/views/${slug}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (slug: string) => fetcher<void>(`/views/${slug}`, { method: 'DELETE' }),
      records: (viewSlug: string, query?: Record<string, string>) => {
        const params = query ? new URLSearchParams(query) : new URLSearchParams();
        return fetcher<Contracts.ViewRecords>(`/views/${viewSlug}/records?${params.toString()}`);
      },
      record: (viewSlug: string, id: string) => fetcher<unknown>(`/views/${viewSlug}/records/${id}`),
    },
    auth: {
      getProfile: () => fetcher<unknown>('/auth/me'),
      updateProfile: (data: { name?: string; email?: string }) =>
        fetcher<unknown>('/auth/me', { method: 'PUT', body: JSON.stringify(data) }),
      changePassword: (data: { currentPassword: string; newPassword: string }) =>
        fetcher<unknown>('/auth/change-password', { method: 'POST', body: JSON.stringify(data) }),
    },
    agentTemplates: {
      list: () => fetcher<unknown[]>('/ai/agent-templates'),
      instantiate: (
        templateId: string,
        overrides?: { name?: string; description?: string },
      ) =>
        fetcher<{ agent?: AgentResponse; id?: string }>(`/ai/agent-templates/${templateId}/instantiate`, {
          method: 'POST',
          body: JSON.stringify(overrides ?? {}),
        }),
    },
    tasks: {
      list: (status?: string) => fetcher<import('@/app/tasks/types').Task[]>(`/tasks?limit=100${status ? `&status=${encodeURIComponent(status)}` : ''}`),
      get: (id: string) => fetcher<import('@/app/tasks/types').TaskDetail>(`/tasks/${encodeURIComponent(id)}`),
      create: (data: { title: string; instructions: string; idempotency_key: string }) => fetcher<import('@/app/tasks/types').Task>('/tasks', { method: 'POST', body: JSON.stringify(data) }),
      authorize: (id: string, data: import('@/app/tasks/types').AuthorizationInput) => fetcher<import('@/app/tasks/types').AuthorizationResult>(`/tasks/${encodeURIComponent(id)}/authorize`, { method: 'POST', body: JSON.stringify(data) }),
      revoke: (id: string, authorizationId: string) => fetcher(`/tasks/${encodeURIComponent(id)}/authorize/${encodeURIComponent(authorizationId)}/revoke`, { method: 'POST' }),
      run: (id: string, authorizationId: string, idempotencyKey: string, expectedSelectionVersion: number) => fetcher(`/tasks/${encodeURIComponent(id)}/runs`, { method: 'POST', body: JSON.stringify({ authorization_id: authorizationId, idempotency_key: idempotencyKey, expected_selection_version: expectedSelectionVersion }) }),
      accept: (id: string, runId: string, resultHash: string) => fetcher(`/tasks/${encodeURIComponent(id)}/accept`, { method: 'POST', body: JSON.stringify({ run_id: runId, result_hash: resultHash }) }),
      reject: (id: string, runId: string, reason: string, expectedSelectionVersion: number) => fetcher(`/tasks/${encodeURIComponent(id)}/runs/${encodeURIComponent(runId)}/reject`, { method: 'POST', body: JSON.stringify({ reason, expected_selection_version: expectedSelectionVersion }) }),
      reopen: (id: string, reason: string, expectedSelectionVersion: number) => fetcher(`/tasks/${encodeURIComponent(id)}/reopen`, { method: 'POST', body: JSON.stringify({ reason, expected_selection_version: expectedSelectionVersion }) }),
      cancel: (id: string) => fetcher(`/tasks/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: JSON.stringify({ reason: 'cancelled from tasks page' }) }),
    },
    agentCommunications: {
      rename: (id: string, name: string) => fetcher(`/external-agents/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
      disable: (id: string) => fetcher(`/external-agents/${id}/disable`, { method: 'POST' }),
      get: () => fetcher<import('@/app/agents/communications/types').Communications>('/external-agents/communications'),
      updateCard: (agentSlug: string, body: { description: string; examples: string[] }) => fetcher(`/agents/${encodeURIComponent(agentSlug)}/card`, { method: 'PUT', body: JSON.stringify(body) }),
      createGrant: (agentSlug: string, body: { grantee_external_agent_id: string; scopes: string[]; max_turns_per_hour: number; acknowledge_data_sharing: true }) => fetcher(`/agents/${encodeURIComponent(agentSlug)}/grants`, { method: 'POST', body: JSON.stringify(body) }),
      revokeGrant: (agentSlug: string, grantId: string) => fetcher(`/agents/${encodeURIComponent(agentSlug)}/grants/${encodeURIComponent(grantId)}`, { method: 'DELETE' }),
    },
    aiAgents: {
      list: () => fetcher<Contracts.AgentList>('/agents-config'),
      get: (id: string) => fetcher<AgentResponse>(`/agents-config/${id}`),
      create: (data: {
        name: string;
        kind?: 'cloud' | 'assistant';
        description?: string;
        systemPrompt: string;
        provider?: string;
        model?: string;
        toolsScope?: Record<string, string[]>;
        channelId?: string;
        fallbackMode?: 'llm' | 'message' | 'silent';
        fallbackMessage?: string;
        maxSteps?: number;
        settings?: { maxOutputTokens?: number };
        knowledgeEnabled?: boolean;
      }) => fetcher<AgentResponse>('/agents-config', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: {
        kind?: 'cloud' | 'assistant';
        name?: string;
        description?: string;
        systemPrompt?: string;
        provider?: string;
        model?: string;
        toolsScope?: Record<string, string[]>;
        channelId?: string;
        fallbackMode?: 'llm' | 'message' | 'silent';
        fallbackMessage?: string;
        maxSteps?: number;
        settings?: { maxOutputTokens?: number; voice?: { clientKnowledgeDocumentIds?: string[] } };
        status?: 'active' | 'paused' | 'archived';
        knowledgeEnabled?: boolean;
      }) => fetcher<AgentResponse>(`/agents-config/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      archive: (id: string) => fetcher<AgentResponse>(`/agents-config/${id}`, { method: 'DELETE' }),
      test: (tenantSlug: string, agentSlug: string, message: string) =>
        fetcher<unknown>(`/tenants/${tenantSlug}/agents/${agentSlug}/test`, {
          method: 'POST',
          body: JSON.stringify({ message }),
        }),
      run: (tenantSlug: string, agentSlug: string, message: string, sessionId?: string, debug?: boolean) =>
        fetcher<unknown>(`/tenants/${tenantSlug}/agents/${agentSlug}/run`, {
          method: 'POST',
          body: JSON.stringify({ message, ...(sessionId ? { session_id: sessionId } : {}), ...(debug ? { debug: true } : {}) }),
        }),
      /** Partial update: soul / memoryEnabled / schedulesEnabled / settings patch. */
      patch: (id: string, data: Record<string, unknown>) =>
        fetcher<AgentResponse>(`/agents-config/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      builder: {
        chat: (data: {
          messages: Array<{ role: 'user' | 'assistant'; content: string }>;
          agent_id?: string;
          focus?: AgentBuilderFocus;
          locale?: string;
        }) => fetcher<AgentBuilderChatResult>('/agents/builder/chat', { method: 'POST', body: JSON.stringify(data) }),
        apply: (data: { proposal: AgentBuilderProposal; change_ids: string[]; locale?: string; confirm_warnings?: boolean }) =>
          fetcher<AgentBuilderApplyResult>('/agents/builder/apply', { method: 'POST', body: JSON.stringify(data) }),
      },
      voiceStatus: (agentId: string) =>
        fetcher<AgentVoiceStatus>(`/agents/${agentId}/voice/status`),
      schedules: {
        list: (agentId: string) => fetcher<AgentSchedule[]>(`/agents/${agentId}/schedules`),
        create: (agentId: string, data: Partial<AgentScheduleInput>) =>
          fetcher<AgentSchedule>(`/agents/${agentId}/schedules`, { method: 'POST', body: JSON.stringify(data) }),
        update: (agentId: string, id: string, data: Partial<AgentScheduleInput>) =>
          fetcher<AgentSchedule>(`/agents/${agentId}/schedules/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
        remove: (agentId: string, id: string) =>
          fetcher<{ id: string; deleted: boolean }>(`/agents/${agentId}/schedules/${id}`, { method: 'DELETE' }),
        run: (agentId: string, id: string) =>
          fetcher<Record<string, unknown>>(`/agents/${agentId}/schedules/${id}/run`, { method: 'POST' }),
      },
      memory: {
        list: (agentId: string, kind?: string, limit = 50) =>
          fetcher<{ agentId: string; items: AgentMemoryItem[] }>(
            `/agents/${agentId}/memory?limit=${limit}${kind ? `&kind=${kind}` : ''}`,
          ),
        setWorking: (agentId: string, text: string) =>
          fetcher<{ agentId: string; workingMemory: unknown }>(`/agents/${agentId}/memory/working`, {
            method: 'PUT',
            body: JSON.stringify({ text }),
          }),
        forget: (agentId: string, memoryId: string) =>
          fetcher<{ id: string; forgotten: boolean }>(`/agents/${agentId}/memory/${memoryId}`, { method: 'DELETE' }),
        people: (agentId: string) =>
          fetcher<{ agentId: string; people: AgentPersonItem[] }>(`/agents/${agentId}/people`),
      },
      connections: (agentSlug: string) =>
        fetcher<AgentConnections>(`/agents/${agentSlug}/connections`),
      connectIntegration: (agentSlug: string, slug: AgentConnectionSlug, installationId?: string) =>
        fetcher<Record<string, unknown>>(`/agents/${agentSlug}/channels/${slug}/connect`, {
          method: 'POST',
          body: JSON.stringify(installationId ? { installation_id: installationId } : {}),
        }),
      slackChannels: (agentSlug: string) =>
        fetcher<SlackChannelModes>(`/agents/${agentSlug}/channels/slack/channels`),
      saveSlackChannels: (
        agentSlug: string,
        body: { channels: Record<string, { mode: SlackChannelMode; name?: string }> },
      ) =>
        fetcher<{ channels: Record<string, { mode: SlackChannelMode; name?: string }> }>(
          `/agents/${agentSlug}/channels/slack/channels`,
          { method: 'PUT', body: JSON.stringify(body) },
        ),
      disconnectIntegration: (agentSlug: string, slug: AgentConnectionSlug) =>
        fetcher<{ disconnected: boolean }>(`/agents/${agentSlug}/channels/${slug}/disconnect`, { method: 'DELETE' }),
      composio: {
        bindings: (agentSlug: string) => fetcher<ComposioBindings>(`/agents/${agentSlug}/composio/bindings`),
        setBindings: (agentSlug: string, bindings: Array<{ connection_id: string; tools?: string[] | null }>) =>
          fetcher<ComposioBindingsSaved>(`/agents/${agentSlug}/composio/bindings`, {
            method: 'PUT',
            body: JSON.stringify({ bindings }),
          }),
      },
      provisionExternalAccess: (agentSlug: string, name?: string) =>
        fetcher<{
          endpoint: string;
          api_key: string;
          agent_slug: string;
          expires_at: string;
          warning: string;
        }>('/tenant-users/agent-access', {
          method: 'POST',
          body: JSON.stringify({ agent_slug: agentSlug, ...(name ? { name } : {}) }),
        }),
      updateRules: (id: string, rules: Array<{
        name: string;
        match_type: string;
        match: string;
        response: string;
        priority: number;
      }>) =>
        fetcher<AgentResponse>(`/agents-config/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ rules }),
        }),
    },
    agentRuns: {
      list: (query?: {
        agent_id?: string;
        session_id?: string;
        status?: string;
        date_from?: string;
        date_to?: string;
        limit?: number;
      }) => {
        const params = new URLSearchParams();
        if (query?.agent_id) params.set('agent_id', query.agent_id);
        if (query?.session_id) params.set('session_id', query.session_id);
        if (query?.status) params.set('status', query.status);
        if (query?.date_from) params.set('date_from', query.date_from);
        if (query?.date_to) params.set('date_to', query.date_to);
        if (query?.limit) params.set('limit', String(query.limit));
        return fetcher<Contracts.AgentRun[] & { data?: Contracts.AgentRun[] }>(`/ai/agent-runs?${params.toString()}`);
      },
    },
    agentChannels: {
      list: (slug: string) => fetcher<AgentChannelsResponse>(`/agents/${slug}/channels`),
      connect: (slug: string, channelId: string) =>
        fetcher<unknown>(`/agents/${slug}/channels`, { method: 'POST', body: JSON.stringify({ channelId }) }),
      disconnect: (slug: string, channelId: string) =>
        fetcher<void>(`/agents/${slug}/channels/${channelId}`, { method: 'DELETE' }),
    },
    agentVersions: {
      list: (id: string) => fetcher<AgentVersionResponse[]>(`/agents-config/${id}/versions`),
      rollback: (id: string, version: number | undefined) =>
        fetcher<{ versionNumber: number; systemPrompt: string; rules: unknown[] }>(`/agents-config/${id}/rollback/${version}`, { method: 'POST' }),
    },
    tenantAiProviders: {
      list: (tenantId: string) => fetcher<TenantAiProvider[]>(`/auth/tenants/${tenantId}/ai-providers`),
      create: (tenantId: string, data: { name: string; type: string; provider_type?: string; base_url: string; api_key?: string; default_model?: string; priority?: number }) =>
        fetcher<TenantAiProvider>(`/auth/tenants/${tenantId}/ai-providers`, { method: 'POST', body: JSON.stringify(data) }),
      update: (tenantId: string, providerId: string, data: Partial<{ name: string; provider_type: string; base_url: string; api_key?: string; default_model?: string; priority?: number }>) =>
        fetcher<TenantAiProvider>(`/auth/tenants/${tenantId}/ai-providers/${providerId}`, { method: 'PUT', body: JSON.stringify(data) }),
      test: (tenantId: string, providerId: string) =>
        fetcher<{ status: 'ok'; test_latency_ms: number; models_available: string[] }>(
          `/auth/tenants/${tenantId}/ai-providers/${providerId}/test`,
          { method: 'POST' },
        ),
      delete: (tenantId: string, providerId: string) =>
        fetcher<void>(`/auth/tenants/${tenantId}/ai-providers/${providerId}`, { method: 'DELETE' }),
    },
    aiCallLogs: {
      list: (query?: {
        provider?: string;
        model?: string;
        source_type?: string;
        source_id?: string;
        status?: string;
        since?: string;
        date_from?: string;
        date_to?: string;
        limit?: number;
        offset?: number;
      }) => {
        const params = new URLSearchParams();
        if (query?.provider) params.set('provider', query.provider);
        if (query?.model) params.set('model', query.model);
        if (query?.source_type) params.set('source_type', query.source_type);
        if (query?.source_id) params.set('source_id', query.source_id);
        if (query?.status) params.set('status', query.status);
        if (query?.since) params.set('since', query.since);
        if (query?.date_from) params.set('date_from', query.date_from);
        if (query?.date_to) params.set('date_to', query.date_to);
        if (query?.limit) params.set('limit', String(query.limit));
        if (query?.offset) params.set('offset', String(query.offset));
        return fetcher<unknown>(`/ai/logs?${params.toString()}`);
      },
      get: (id: string) => fetcher<unknown>(`/ai/logs/${id}`),
    },
  };

// Additional APIs for new pages (appended)
export const apiExt = {
  aiCost: {
    dashboard: () => fetcher<unknown>('/ai/cost-dashboard'),
    daily: () => fetcher<{ days: Array<{ date: string; cost: number; calls: number; tokens: number }> }>('/ai/cost-dashboard/daily'),
    top: () => fetcher<unknown>('/ai/cost-dashboard/top'),
  },
  aiTemplates: {
    list: () => fetcher<Contracts.AiTemplate[]>('/ai/templates'),
    get: (id: string) => fetcher<Contracts.AiTemplate>(`/ai/templates/${id}`),
    create: (data: { name: string; slug?: string; type: 'system' | 'prompt'; content: string; variables?: string[] }) =>
      fetcher<Contracts.AiTemplate>('/ai/templates', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: { name?: string; type?: string; content?: string; variables?: string[] }) =>
      fetcher<Contracts.AiTemplate>(`/ai/templates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: string) => fetcher<void>(`/ai/templates/${id}`, { method: 'DELETE' }),
  },
  composio: {
    catalog: () => fetcher<{ installation_id: string; entries: ComposioCatalogEntry[] }>('/composio/catalog'),
    connections: () => fetcher<ComposioConnectionsState>('/composio/connections'),
    createConnection: (body: { toolkit: string; name?: string }) =>
      fetcher<{ connection: ComposioConnection; catalog: Omit<ComposioCatalogEntry, 'toolkit' | 'auth_config_id'> | null }>(
        '/composio/connections',
        { method: 'POST', body: JSON.stringify(body) },
      ),
    deleteConnection: (id: string) => fetcher<{ deleted: boolean }>(`/composio/connections/${id}`, { method: 'DELETE' }),
    connect: (id: string, body: { fields?: Record<string, string>; reconnect?: boolean } = {}) =>
      fetcher<ComposioConnectResult>(`/composio/connections/${id}/connect`, { method: 'POST', body: JSON.stringify(body) }),
    check: (id: string, body: { keep_latest?: boolean } = {}) =>
      fetcher<ComposioCheckResult>(`/composio/connections/${id}/check`, { method: 'POST', body: JSON.stringify(body) }),
    tools: (id: string) =>
      fetcher<{ connection_id: string; toolkit: string; tools: ComposioToolOption[] }>(`/composio/connections/${id}/tools`),
  },
  integrations: {
    list: () => fetcher<Contracts.Integration[]>('/integrations'),
    get: (slug: string) => fetcher<Contracts.Integration>(`/integrations/${slug}`),
    install: (slug: string, config?: Record<string, string>) =>
      fetcher<{ installation_id: string }>(`/integrations/${slug}/install`, { method: 'POST', body: JSON.stringify({ config: config ?? {} }) }),
    uninstall: (slug: string) =>
      fetcher<Contracts.Integration>(`/integrations/${slug}`, { method: 'DELETE' }),
    configure: (slug: string, data: Record<string, string>) =>
      fetcher<unknown>(`/integrations/${slug}/configure`, { method: 'POST', body: JSON.stringify({ config: data }) }),
    activate: (slug: string) =>
      fetcher<unknown>(`/integrations/${slug}/activate`, { method: 'POST' }),
    activateInstance: (slug: string, installationId: string) =>
      fetcher<unknown>(`/integrations/${slug}/instances/${installationId}/activate`, { method: 'POST' }),
    deactivate: (slug: string) =>
      fetcher<unknown>(`/integrations/${slug}/deactivate`, { method: 'POST' }),
    test: (slug: string) =>
      fetcher<unknown>(`/integrations/${slug}/test`, { method: 'POST' }),
    slackAuthorizeUrl: (installationId?: string) =>
      fetcher<{ authorize_url: string }>(`/integrations/slack/oauth/start${installationId ? `?installation_id=${encodeURIComponent(installationId)}` : ''}`),
    instanceLogs: (slug: string, installationId: string, limit = 5) =>
      fetcher<Array<{ id: string; action: string; status: 'success' | 'failure'; created_at: string }>>(
        `/integrations/${slug}/instances/${installationId}/logs?limit=${limit}`,
      ),
    logs: (slug: string, limit = 50) =>
      fetcher<Contracts.IntegrationLog[]>(`/integrations/${slug}/logs?limit=${limit}`),
  },
  agentMessages: {
    // NOTE: these 3 endpoints return { success, ...data } without a `data` wrapper,
    // so we use raw fetch instead of fetcher() which always unwraps json.data.
    listAll: async (tenantSlug: string, params?: {
      status?: 'pending' | 'read' | 'archived' | 'all';
      priority?: 'normal' | 'high' | 'urgent';
      agent?: string;
      from?: string;
      to?: string;
      since?: string;
      until?: string;
      page?: number;
      limit?: number;
      sort?: 'created_at' | 'priority';
      order?: 'asc' | 'desc';
    }): Promise<{ messages: Contracts.AgentMessage[]; pagination: { page: number; limit: number; total: number; pages: number } }> => {
      const p = new URLSearchParams();
      if (params?.status) p.set('status', params.status);
      if (params?.priority) p.set('priority', params.priority);
      if (params?.agent) p.set('agent', params.agent);
      if (params?.from) p.set('from', params.from);
      if (params?.to) p.set('to', params.to);
      if (params?.since) p.set('since', params.since);
      if (params?.until) p.set('until', params.until);
      if (params?.page !== undefined) p.set('page', String(params.page));
      if (params?.limit !== undefined) p.set('limit', String(params.limit));
      if (params?.sort) p.set('sort', params.sort);
      if (params?.order) p.set('order', params.order);
      const qs = p.toString();
      const url = `${getApiUrl()}/tenants/${tenantSlug}/agent-messages/all${qs ? `?${qs}` : ''}`;
      const auth = getAuthHeader() as Record<string, string>;
      const res = await fetch(url, { headers: { 'Content-Type': 'application/json', ...auth } });
      if (res.status === 401) {
        if (globalThis.window !== undefined) { localStorage.clear(); globalThis.location.href = '/login'; }
        throw new Error('Session expired. Please login again.');
      }
      const json = await res.json() as { success: boolean; messages?: Contracts.AgentMessage[]; pagination?: { page: number; limit: number; total: number; pages: number }; error?: string };
      if (!res.ok) throw new Error(typeof json.error === 'string' ? json.error : 'Error al cargar mensajes');
      return { messages: json.messages ?? [], pagination: json.pagination ?? { page: 1, limit: 50, total: 0, pages: 0 } };
    },
    stats: async (tenantSlug: string): Promise<{ total_pending: number; total_read: number; total_archived: number; by_agent: Record<string, { pending: number; read: number }>; by_priority: Record<string, number> }> => {
      const url = `${getApiUrl()}/tenants/${tenantSlug}/agent-messages/stats`;
      const auth = getAuthHeader() as Record<string, string>;
      const res = await fetch(url, { headers: { 'Content-Type': 'application/json', ...auth } });
      if (res.status === 401) {
        if (globalThis.window !== undefined) { localStorage.clear(); globalThis.location.href = '/login'; }
        throw new Error('Session expired. Please login again.');
      }
      const json = await res.json() as { success: boolean; total_pending?: number; total_read?: number; total_archived?: number; by_agent?: Record<string, { pending: number; read: number }>; by_priority?: Record<string, number>; error?: string };
      if (!res.ok) throw new Error(typeof json.error === 'string' ? json.error : 'Error al cargar estadísticas');
      return {
        total_pending: json.total_pending ?? 0,
        total_read: json.total_read ?? 0,
        total_archived: json.total_archived ?? 0,
        by_agent: json.by_agent ?? {},
        by_priority: json.by_priority ?? {},
      };
    },
    thread: async (tenantSlug: string, messageId: string): Promise<{ thread: Contracts.AgentMessage[]; root_id: string }> => {
      const url = `${getApiUrl()}/tenants/${tenantSlug}/agent-messages/${messageId}/thread`;
      const auth = getAuthHeader() as Record<string, string>;
      const res = await fetch(url, { headers: { 'Content-Type': 'application/json', ...auth } });
      if (res.status === 401) {
        if (globalThis.window !== undefined) { localStorage.clear(); globalThis.location.href = '/login'; }
        throw new Error('Session expired. Please login again.');
      }
      const json = await res.json() as { success: boolean; thread?: Contracts.AgentMessage[]; root_id?: string; error?: string };
      if (!res.ok) throw new Error(typeof json.error === 'string' ? json.error : 'Error al cargar hilo');
      return { thread: json.thread ?? [], root_id: json.root_id ?? '' };
    },
    send: async (
      tenantSlug: string,
      body: {
        to_agent: string;
        subject?: string;
        payload: Record<string, unknown>;
        priority?: 'normal' | 'high' | 'urgent';
        in_reply_to?: string | null;
        auto_run?: boolean;
      },
    ): Promise<{ id: string }> => {
      const url = `${getApiUrl()}/tenants/${tenantSlug}/agent-messages/send`;
      const auth = getAuthHeader() as Record<string, string>;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({ ...body, auto_run: body.auto_run ?? true }),
      });
      if (res.status === 401) {
        if (globalThis.window !== undefined) { localStorage.clear(); globalThis.location.href = '/login'; }
        throw new Error('Session expired. Please login again.');
      }
      const json = await res.json() as { success: boolean; data?: { id: string }; id?: string; error?: string };
      if (!res.ok) throw new Error(typeof json.error === 'string' ? json.error : 'Error al enviar mensaje');
      return { id: json.data?.id ?? json.id ?? '' };
    },
  },
  knowledgeBulk: {
    uploadFiles: async (files: File[]) => {
      const formData = new FormData();
      files.forEach((f) => formData.append('files', f));
      const headers = getAuthHeader() as Record<string, string>;
      const res = await fetch(`${getApiUrl()}/knowledge/documents/upload/bulk`, {
        method: 'POST',
        headers,
        body: formData,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Bulk upload failed');
      return json.data;
    },
    indexUrls: (items: Array<{ url: string; title?: string }>) =>
      fetcher<{ results: Contracts.BulkKnowledgeResult[] }>('/knowledge/documents/bulk', { method: 'POST', body: JSON.stringify({ items }) }),
  },
};

